import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { CircularProgress, Box } from '@mui/material';
import type { User } from '../../services/types/auth';

interface ProtectedRouteProps {
    children: React.ReactNode | ((props: { user: User }) => React.ReactNode);
    roles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
    const { user, isLoading, isAuthenticated } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!isAuthenticated || !user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!user.is_verified) {
        return <Navigate to="/verification-pending" state={{ email: user.email }} replace />;
    }

    if (roles && !roles.includes(user.role)) {
        // Redirect based on role
        if (user.role === 'admin') {
            return <Navigate to="/admin" replace />;
        } else if (user.role === 'content_creator') {
            return <Navigate to="/content-creator" replace />;
        } else {
            return <Navigate to="/student" replace />;
        }
    }

    if (typeof children === 'function') {
        return <>{children({ user })}</>;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
